import React, { Fragment } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { graphql, useLazyLoadQuery } from 'react-relay';

import FolderClosedIcon from '@atlaskit/icon/core/folder-closed';
import PageIcon from '@atlaskit/icon/core/page';
import { EmptyState } from '@atlassian/search-dialog/empty-state';
import {
	ResultContainer,
	SearchDialogContent,
} from '@atlassian/search-dialog/search-dialog.styled';
import { SearchResult } from '@atlassian/search-dialog/search-result';

import {
	type ArticleSearchResultItem,
	useArticleSearch,
} from '../../../../controllers/use-article-search';
import { ContentType } from '../../types';

import type { resolvedAndErrorContentQuery } from './__generated__/resolvedAndErrorContentQuery.graphql';
import { SearchErrorImage } from './error-image';
import { messages } from './messages';

type SearchDropdownProps = {
	cloudId: string;
	helpCenterAri: string;
	queryWord: string;
};

export const ResolvedAndErrorContent = ({
	cloudId,
	helpCenterAri,
	queryWord,
}: SearchDropdownProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	/* eslint-disable @atlassian/relay/unused-fields */
	const searchResponse = useLazyLoadQuery<resolvedAndErrorContentQuery>(
		graphql`
			query resolvedAndErrorContentQuery(
				$cloudId: String!
				$helpCenterAri: String!
				$input: HelpObjectStoreSupportSiteArticleSearchInput!
				$first: Int
			) {
				helpObjectStore_searchArticlesForSupportSite(
					cloudId: $cloudId
					helpCenterAri: $helpCenterAri
					input: $input
					first: $first
				)
					@connection(
						key: "ResolvedAndErrorContent__helpObjectStore_searchArticlesForSupportSite"
					) {
					edges {
						node {
							... on HelpObjectStoreSupportSiteArticleSearchResult {
								id
								title
								contentType
								urlInfo {
									shareUrl
								}
							}
							... on QueryError {
								message
							}
						}
					}
				}
			}
		`,
		{
			cloudId,
			helpCenterAri,
			input: { query: queryWord, contentTypes: ['FOLDER', 'PAGE'] },
			first: 10,
		},
	);

	/* eslint-enable @atlassian/relay/unused-fields */
	const { articlesAndFolders, hasQueryError } = useArticleSearch(searchResponse, queryWord);

	const showEmptyState = articlesAndFolders && articlesAndFolders.length === 0;
	const showResolvedState = articlesAndFolders && articlesAndFolders.length > 0;

	return (
		<Fragment>
			{(showEmptyState || hasQueryError) && (
				<EmptyState
					title={
						hasQueryError
							? formatMessage(messages.errorTitle)
							: formatMessage(messages.emptyStateTitle)
					}
					Image={SearchErrorImage}
					content={
						hasQueryError ? (
							<FormattedMessage {...messages.errorDescription} />
						) : (
							<FormattedMessage {...messages.emptyStateDescription} />
						)
					}
				/>
			)}
			{showResolvedState && (
				<>
					<SearchDialogContent>
						<ResultContainer>
							{articlesAndFolders.map((result: ArticleSearchResultItem) => (
								<SearchResult
									key={result.id}
									href={result.url}
									title={result.title}
									isCollapsed
									icon={
										result.type === ContentType.PAGE ? (
											<PageIcon label="" />
										) : (
											<FolderClosedIcon label="" />
										)
									}
								/>
							))}
						</ResultContainer>
					</SearchDialogContent>
				</>
			)}
		</Fragment>
	);
};
